'use client';

import { useContext, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import type { FormikHelpers } from 'formik';

import { Form } from 'src/shared/components/form';
import { Typography } from 'src/shared/ui/Typography';
import { Button } from 'src/shared/ui/Button';

import { ArrowIcon } from 'src/shared/icons';

import { useMutationSignAgreement } from 'src/dal/services/user/userQueries';
import { getApiError } from 'src/shared/utils/getApiError';

import { ApplicantFormContext } from './blocks/ApplicantFormProvider';
import { ResendSmsButton } from './blocks/ResendSmsButton';

import { secondStepValidationSchema } from './validationSchema';
import { digitsOnly } from './utils';

import styles from '../client-component.module.css';

export type VerificationCodeFormValues = {
  phone?: string;
  verificationCode: string;
};

export const VerificationCodeForm = () => {
  const searchParams = useSearchParams();

  const { values: applicantValues, setCurrentStep } = useContext(ApplicantFormContext);

  const recruiterId = searchParams.get('recruiter_id');
  const legalId = searchParams.get('legal_id');

  const [isSignedSuccess, setIsSignedSuccess] = useState(false);

  const signAgreementMutation = useMutationSignAgreement({
    onSuccess: () => setIsSignedSuccess(true),
  });

  const handleSubmit = async (
    values: VerificationCodeFormValues,
    helpers: FormikHelpers<VerificationCodeFormValues>,
  ) => {
    if (!applicantValues || !applicantValues.agreementTypeIds?.length || !recruiterId || !legalId) {
      // TODO: Обработка ошибки отсутствия необходимых данных
      return;
    }

    try {
      await signAgreementMutation.mutateAsync({
        recruiterId: Number(recruiterId),
        legalId,
        agreementTypeIds: applicantValues.agreementTypeIds,
        applicantFirstName: applicantValues.applicantFirstName,
        applicantLastName: applicantValues.applicantLastName,
        applicantMiddleName: applicantValues.applicantMiddleName,
        applicantPhone: digitsOnly(applicantValues.phone),
        verificationCode: digitsOnly(values.verificationCode),
      });
    } catch (error) {
      // Ошибка API показывается пользователю под полем кода и не логируется: это ожидаемый сценарий валидации
      helpers.setFieldError('verificationCode', getApiError(error));
      helpers.setFieldTouched('verificationCode', true, false);
    }
  };

  return (
    <>
      <div className={styles.title}>
        <button
          type="button"
          className={styles.arrow}
          aria-label="Вернуться к анкете"
          onClick={() => setCurrentStep(1)}
        >
          <ArrowIcon />
        </button>
        <Typography view="semibold_24">Подтверждение телефона</Typography>
      </div>

      <Form<VerificationCodeFormValues>
        initialValues={{
          phone: applicantValues?.phone,
          verificationCode: '',
        }}
        validationSchema={secondStepValidationSchema}
        onSubmit={handleSubmit}
      >
        <Form.Fields>
          <Form.Input name="phone" label={<Typography view="semibold_14">Номер телефона</Typography>} readOnly />

          <Form.MaskedInput
            name="verificationCode"
            label={<Typography view="semibold_14">Код из SMS</Typography>}
            mask={[/\d/, ' ', /\d/, ' ', /\d/, ' ', /\d/]}
            placeholder="_ _ _ _"
            readOnly={isSignedSuccess}
          />
        </Form.Fields>

        {isSignedSuccess ? (
          <div className={styles.successMessage}>
            <Typography view="semibold_24" color="var(--color-green)">
              Номер успешно подтвержден!
            </Typography>
            <Typography view="semibold_24" color="var(--color-green)">
              Документы подписаны!
            </Typography>
          </div>
        ) : (
          <>
            <ResendSmsButton />

            <Button type="submit" className={styles.submitButton} stretched disabled={signAgreementMutation.isPending}>
              Подтвердить
            </Button>
          </>
        )}
      </Form>
    </>
  );
};
